import { appLog } from '~src/utils/log';
import { apiCweb } from './api-cweb';
import { SecondaryShareKeyVM } from '~src/types/db';
import { DB_STATE } from '~src/types/enums';

/**
 * Primary-/Secondary Share Keys feature.
 *
 * Only `activateSecondaryKeys` is wired into the UI today (Vendors tab
 * activate toggle). The rest are added now, ready for the Subcontractors-tab
 * work later this week, following the New APIs confirmed by backend:
 *
 * - POST /v1/projects/{project_id}/tenders/{tender_id}/share/secondary_key
 * - GET/POST /v1/tender_shares/{share_id}/secondary_share_keys
 * - PUT /v1/tender_shares/{share_id}/secondary_share_keys/{secondary_share_key}?action=update
 * - PUT /v1/tender_shares/{share_id}/secondary_share_keys/{secondary_share_key}?action=remove|reactivate
 * - PATCH /v1/tender_shares/{share_id}/secondary_share_keys/invite_messages
 * - POST /v1/tender_shares/{share_id}/secondary_share_keys/invite_messages_template_preview_as_html
 *
 * Every endpoint below the activate call requires the acting Primary
 * Share Key's `Share-Key` header (matches the convention already used
 * throughout `shares.ts`, e.g. `claimSharedTender`, `getShareStatus`).
 */

export interface ActivateSecondaryKeysParams {
  projectId: number | string;
  tenderId: number | string;
}

/**
 * Enable secondary share key invitation for the tender registration.
 * This action is immutable - there is no endpoint to deactivate it.
 */
export const activateSecondaryKeys = ({
  projectId,
  tenderId,
}: ActivateSecondaryKeysParams): Promise<void> => {
  appLog('activateSecondaryKeys ->', { projectId, tenderId });
  return apiCweb
    .post(`/projects/${projectId}/tenders/${tenderId}/share/secondary_key`, {})
    .then(() => undefined);
};

export interface LoadSecondaryShareKeysParams {
  shareId: string;
  shareKey: string;
  /** valid values here: AVAILABLE(3), ACCEPTED(4), REJECTED(5), REMOVED(9) */
  states?: DB_STATE[];
  email?: string;
}

/** List secondary share keys under the current primary share key. */
export const loadSecondaryShareKeys = async ({
  shareId,
  shareKey,
  states,
  email,
}: LoadSecondaryShareKeysParams) => {
  appLog('loadSecondaryShareKeys ->', { shareId, states, email });
  const res = await apiCweb.get<SecondaryShareKeyVM[]>(
    `/tender_shares/${shareId}/secondary_share_keys`,
    {
      params: { states: states?.join(','), email },
      headers: { 'Share-Key': shareKey },
    }
  );
  return res.data;
};

export interface TenderShareKeyCrUpVM {
  email?: string | null;
  title?: string | null;
}

export interface CreateSecondaryShareKeysParams {
  shareId: string;
  shareKey: string;
  body: TenderShareKeyCrUpVM[];
}

/** Create secondary share keys; an invitation email is sent automatically. */
export const createSecondaryShareKeys = ({
  shareId,
  shareKey,
  body,
}: CreateSecondaryShareKeysParams): Promise<void> => {
  appLog('createSecondaryShareKeys ->', { shareId, body });
  return apiCweb
    .post(`/tender_shares/${shareId}/secondary_share_keys`, body, {
      headers: { 'Share-Key': shareKey },
    })
    .then(() => undefined);
};

export interface UpdateSecondaryShareKeyParams {
  shareId: string;
  shareKey: string;
  secondaryShareKey: string;
  data: TenderShareKeyCrUpVM;
}

/** Update a secondary share key (email cannot change once claimed). */
export const updateSecondaryShareKey = ({
  shareId,
  shareKey,
  secondaryShareKey,
  data,
}: UpdateSecondaryShareKeyParams): Promise<void> => {
  appLog('updateSecondaryShareKey ->', { shareId, secondaryShareKey });
  return apiCweb
    .put(
      `/tender_shares/${shareId}/secondary_share_keys/${secondaryShareKey}`,
      data,
      {
        params: { action: 'update' },
        headers: { 'Share-Key': shareKey },
      }
    )
    .then(() => undefined);
};

export interface ChangeSecondaryShareKeyStateParams {
  shareId: string;
  shareKey: string;
  secondaryShareKey: string;
  action: 'remove' | 'reactivate';
}

/**
 * Remove (-> Removed) or reactivate (-> Accepted if already claimed, else
 * Available) a secondary share key.
 */
export const changeSecondaryShareKeyState = ({
  shareId,
  shareKey,
  secondaryShareKey,
  action,
}: ChangeSecondaryShareKeyStateParams): Promise<void> => {
  appLog('changeSecondaryShareKeyState ->', {
    shareId,
    secondaryShareKey,
    action,
  });
  return apiCweb
    .put(
      `/tender_shares/${shareId}/secondary_share_keys/${secondaryShareKey}`,
      {},
      {
        params: { action },
        headers: { 'Share-Key': shareKey },
      }
    )
    .then(() => undefined);
};

export interface TenderShareKeyInviteMessageWithKeyVM {
  /** GUID of the secondary share key to update */
  share_key: string;
  email?: string | null;
  title?: string | null;
}

export interface PatchSecondaryInviteMessagesParams {
  shareId: string;
  shareKey: string;
  body: TenderShareKeyInviteMessageWithKeyVM[];
}

/** Queue secondary share keys for (re)sending their invitation email. */
export const patchSecondaryInviteMessages = ({
  shareId,
  shareKey,
  body,
}: PatchSecondaryInviteMessagesParams): Promise<void> => {
  appLog('patchSecondaryInviteMessages ->', { shareId, body });
  return apiCweb
    .patch(
      `/tender_shares/${shareId}/secondary_share_keys/invite_messages`,
      body,
      { headers: { 'Share-Key': shareKey } }
    )
    .then(() => undefined);
};

export interface PreviewSecondaryInviteMessageParams {
  shareId: string;
  shareKey: string;
}

/** Preview the secondary-key invitation email template as raw HTML. */
export const previewSecondaryInviteMessage = async ({
  shareId,
  shareKey,
}: PreviewSecondaryInviteMessageParams) => {
  appLog('previewSecondaryInviteMessage ->', { shareId });
  const res = await apiCweb.post<string>(
    `/tender_shares/${shareId}/secondary_share_keys/invite_messages_template_preview_as_html`,
    {},
    { headers: { 'Share-Key': shareKey } }
  );
  return res.data;
};
